// community-controller.ts — the COMMUNITY screen, LOGIC half (pure TS, no DOM). One screen with
// three tabs replacing the separate LEADERBOARD and MY PROFILE menu screens:
//   • PLAYERS — the ranked leaderboard (leaderboard-controller.ts is the tab's module, unchanged);
//   • GAMES — recent multiplayer games from the wi-api /stats/games.json feed (new here), with the
//     replay browser's quiet-recording filter so test/no-action games don't drown real ones;
//   • MY PROFILE — the player's own profile (profile-controller.ts is the tab's module, unchanged).
// The sub-controllers keep their own node tests; this controller owns the tab state, the GAMES tab
// data, and the cross-tab plumbing (a sign-in on the profile tab lights the players tab's rank pin).

import { LeaderboardController, type LoadStatus } from './leaderboard-controller.ts';
import { ProfileController, type ProfileSignInResult } from './profile-controller.ts';
import { openReplayViewer } from '../ui/replaylink.ts';

export type CommunityTab = 'players' | 'games' | 'profile';

/** One recent game (mirrors CommunityGame in workers/leaderboard/src/games.ts). */
export interface CommunityGameRow {
  key: string;
  map: string;
  startUtc: number;
  endUtc: number;
  durationSecs: number;
  players: Array<{ name: string; winner: boolean; anon: boolean; registered: boolean }>;
  bots: number;
  replayKey: string;
  hasReplay: boolean;
  replaySize: number;
}
export interface CommunityGamesSummary {
  today: number;
  week: number;
  topMap: string | null;
}

// The replay browser's action proxy (replay-browse.ts QUIET_BYTES): a .wirep grows with issued
// commands, so a sub-50KB recording is a test/no-action game. Same threshold here so the GAMES tab
// and the viewer's browse panel agree on what "quiet" means.
export const QUIET_GAME_BYTES = 50 * 1024;
/** localStorage key for the show-all toggle (persisted like the viewer's wiReplayBrowseAll). */
export const SHOW_ALL_GAMES_KEY = 'wiCommunityGamesAll';

export interface CommunityDeps {
  /** GET seam (the SAME shape as the lobby / leaderboard httpGet) — production: fetch; tests: scripted. */
  httpGet(url: string): Promise<{ body: string | null; error?: string }>;
  /** base of the JSON endpoints, e.g. 'https://api.warfareincorporated.com/stats' (no trailing slash). */
  baseUrl: string;
  /** the view re-renders on every state change. */
  onChange(): void;
  /** the current non-anonymous stats username ('' when anonymous) at open time — kept live across
   *  the profile tab's sign-in/out so the players tab's rank pin follows. */
  playerName: string;
  /** validate + persist credentials (the profile tab's sign-in gate) — main.ts signInForProfile. */
  signIn(name: string, password: string): Promise<ProfileSignInResult>;
  /** clear the persisted identity app-wide — main.ts signOutEverywhere. */
  signOut?(): void;
  /** open an external URL (REGISTER / FORGOT ACCOUNT pages; the replay-viewer fallback). */
  openUrl?(url: string): void;
  /** play a replay IN-CLIENT (the embedded viewer). Absent → fall back to opening the standalone
   *  viewer via openUrl/window.open (the pre-M4 behavior). */
  watchReplay?(replayKey: string): void;
  /** show-all persistence seam — production: localStorage; tests: a map. Absent → not persisted. */
  storage?: { get(key: string): string | null; set(key: string, value: string): void };
  /** rows per leaderboard page (default 25). */
  count?: number;
  /** rows per GAMES page AND the auto-fill target: pages keep fetching until this many games are
   *  VISIBLE through the quiet filter (default 50 — the server's per-request cap). */
  gamesCount?: number;
}

export class CommunityController {
  tab: CommunityTab = 'players';

  /** the PLAYERS tab module — the unchanged leaderboard controller (sorts, rank pin, detail modal). */
  readonly players: LeaderboardController;
  /** the MY PROFILE tab module — the unchanged profile controller (sign-in gate, own profile). */
  readonly profile: ProfileController;

  // ── the GAMES tab state ──────────────────────────────────────────────────────
  gamesStatus: LoadStatus = 'loading';
  gamesError = '';
  games: CommunityGameRow[] = [];
  summary: CommunityGamesSummary | null = null;
  totalGames = 0;
  /** true → quiet (test/no-action) games are shown too. Persisted via deps.storage. */
  showAllGames = false;
  /** a MORE fetch is in flight (the first page uses gamesStatus='loading' instead). */
  loadingMore = false;

  private readonly d: CommunityDeps;
  /** the deps object handed to the players module — OWNED here so sign-in/out can update
   *  playerName in place (the leaderboard reads it per-fetch for the &me= pin + row highlight). */
  private readonly playersDeps: { playerName: string };
  private gamesEpoch = 0;
  private playersStarted = false;
  private gamesStarted = false;
  private profileStarted = false;

  constructor(deps: CommunityDeps) {
    this.d = deps;
    this.showAllGames = deps.storage?.get(SHOW_ALL_GAMES_KEY) === '1';

    const playersDeps = {
      httpGet: deps.httpGet,
      onChange: deps.onChange,
      baseUrl: deps.baseUrl,
      playerName: deps.playerName,
      count: deps.count,
      openUrl: deps.openUrl,
    };
    this.playersDeps = playersDeps;
    this.players = new LeaderboardController(playersDeps);

    this.profile = new ProfileController({
      httpGet: deps.httpGet,
      baseUrl: deps.baseUrl,
      onChange: deps.onChange,
      currentName: () => this.playersDeps.playerName,
      signIn: async (name, password) => {
        const r = await deps.signIn(name, password);
        if (r.kind === 'ok') {
          // signed in app-wide → light the players tab's rank pin / row highlight.
          this.playersDeps.playerName = r.name;
          void this.players.start();
        }
        return r;
      },
      signOut: () => {
        deps.signOut?.();
        this.playersDeps.playerName = '';
        void this.players.start();
      },
      openUrl: deps.openUrl,
    });
  }

  /** Open the screen on `tab` (default PLAYERS) and load that tab. */
  start(tab: CommunityTab = 'players'): void {
    this.setTab(tab);
    if (tab !== 'players' && !this.playersStarted) {
      // warm the default tab too so switching back is instant
      this.playersStarted = true;
      void this.players.start();
    }
  }

  /** Switch tabs; each tab loads on first visit (REFRESH reloads the active one). */
  setTab(tab: CommunityTab): void {
    this.tab = tab;
    if (tab === 'players' && !this.playersStarted) {
      this.playersStarted = true;
      void this.players.start();
    } else if (tab === 'games' && !this.gamesStarted) {
      void this.startGames();
    } else if (tab === 'profile' && !this.profileStarted) {
      this.profileStarted = true;
      this.profile.start();
    }
    this.d.onChange();
  }

  /** REFRESH — reload the active tab. */
  refresh(): void {
    if (this.tab === 'players') this.players.refresh();
    else if (this.tab === 'games') void this.startGames();
    else this.profile.start();
  }

  // ── GAMES tab ────────────────────────────────────────────────────────────────

  private gamesPageSize(): number {
    return this.d.gamesCount ?? 50;
  }

  private gamesUrl(offset: number): string {
    let url = `${this.d.baseUrl}/games.json?count=${this.gamesPageSize()}`;
    if (offset > 0) url += `&offset=${offset}`;
    return url;
  }

  /** Auto-fill: the quiet filter can hide most of a page (the archive is largely test games), so
   *  keep fetching follow-up pages until a full page of games is VISIBLE — clicking MORE just to
   *  see the first screenful is busywork. Bounded: stops at 4 pages fetched (a mostly-quiet
   *  archive can't fetch forever) or when everything is loaded; MORE remains for going deeper. */
  private autoFillGames(): void {
    if (this.showAllGames) return; // nothing hidden → the page is already full
    const target = this.gamesPageSize();
    if (this.visibleGames().length >= target) return;
    if (this.games.length >= target * 4) return;
    if (!this.hasMoreGames()) return;
    void this.moreGames();
  }

  /** Initial GAMES load (or a REFRESH): fetch the first page + the summary strip. */
  async startGames(): Promise<void> {
    this.gamesStarted = true;
    const epoch = ++this.gamesEpoch;
    this.gamesStatus = 'loading';
    this.gamesError = '';
    this.d.onChange();
    const req = await this.d.httpGet(this.gamesUrl(0));
    if (epoch !== this.gamesEpoch) return; // a newer request superseded this one
    if (req.body === null) {
      this.gamesStatus = 'error';
      // 404 = the endpoint isn't served (an older wi-api deploy) — say so plainly instead of
      // echoing the raw status line.
      this.gamesError =
        req.error !== undefined && /\b404\b/.test(req.error)
          ? 'Recent games aren’t available yet.'
          : (req.error ?? 'The games service is unreachable.');
      this.games = [];
      this.summary = null;
      this.d.onChange();
      return;
    }
    try {
      const page = parseGamesPage(req.body);
      this.games = page.games;
      this.summary = page.summary;
      this.totalGames = page.total;
      this.gamesStatus = 'ready';
    } catch {
      this.gamesStatus = 'error';
      this.gamesError = 'The games data could not be read.';
      this.games = [];
      this.summary = null;
    }
    this.d.onChange();
    if (this.gamesStatus === 'ready') this.autoFillGames();
  }

  /** MORE — append the next page (no-op while a fetch is in flight / everything is loaded). */
  async moreGames(): Promise<void> {
    if (this.loadingMore || this.gamesStatus !== 'ready' || !this.hasMoreGames()) return;
    const epoch = this.gamesEpoch;
    this.loadingMore = true;
    this.d.onChange();
    const req = await this.d.httpGet(this.gamesUrl(this.games.length));
    if (epoch !== this.gamesEpoch) return; // a refresh superseded this page
    this.loadingMore = false;
    let appended = false;
    if (req.body !== null) {
      try {
        const page = parseGamesPage(req.body);
        this.games = this.games.concat(page.games);
        this.totalGames = page.total;
        appended = true;
      } catch {
        /* keep what we have; the MORE button stays for a retry */
      }
    }
    this.d.onChange();
    if (appended) this.autoFillGames(); // chain until a full page is visible (bounded)
  }

  hasMoreGames(): boolean {
    return this.games.length < this.totalGames;
  }

  /** A quiet game: a sub-50KB recording (test/no-action, the replay browser's proxy) or an
   *  all-bot roster (automation runs). Hidden by default; SHOW ALL reveals. */
  isQuietGame(g: CommunityGameRow): boolean {
    return (g.hasReplay && g.replaySize < QUIET_GAME_BYTES) || g.players.length === 0;
  }

  /** The GAMES rows the view renders (the quiet filter applied unless SHOW ALL). */
  visibleGames(): CommunityGameRow[] {
    return this.showAllGames ? this.games : this.games.filter((g) => !this.isQuietGame(g));
  }

  hiddenGameCount(): number {
    return this.games.length - this.visibleGames().length;
  }

  toggleShowAllGames(): void {
    this.showAllGames = !this.showAllGames;
    this.d.storage?.set(SHOW_ALL_GAMES_KEY, this.showAllGames ? '1' : '0');
    this.d.onChange();
    // turning the filter back ON can shrink the visible list below a screenful → top it up
    if (!this.showAllGames && this.gamesStatus === 'ready') this.autoFillGames();
  }

  // ── cross-tab plumbing ───────────────────────────────────────────────────────

  /** Open the shared player-detail modal (from a PLAYERS row or a GAMES roster name). */
  openPlayer(name: string): void {
    this.players.openDetail(name);
  }

  /** Watch a replay: in-client when the host provides the seam (M4), else the standalone viewer. */
  watchReplay(replayKey: string): void {
    if (replayKey.length === 0) return;
    if (this.d.watchReplay !== undefined) this.d.watchReplay(replayKey);
    else openReplayViewer(replayKey, this.d.openUrl);
  }
}

function num(v: unknown): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** Defensive JSON → CommunityGameRow[] mapping (the toRow idiom — never trust the wire shape). */
function parseGamesPage(body: string): {
  games: CommunityGameRow[];
  summary: CommunityGamesSummary;
  total: number;
} {
  const obj = JSON.parse(body) as { games?: unknown; summary?: unknown; total?: unknown };
  const rawSummary = (obj.summary ?? {}) as Record<string, unknown>;
  const games = Array.isArray(obj.games)
    ? (obj.games as Record<string, unknown>[]).map(toGameRow)
    : [];
  return {
    games,
    summary: {
      today: num(rawSummary.today),
      week: num(rawSummary.week),
      topMap: rawSummary.topMap == null ? null : String(rawSummary.topMap),
    },
    total: num(obj.total),
  };
}

function toGameRow(m: Record<string, unknown>): CommunityGameRow {
  const players = Array.isArray(m.players)
    ? (m.players as Record<string, unknown>[]).map((p) => ({
        name: String(p.name ?? ''),
        winner: p.winner === true,
        anon: p.anon === true,
        registered: p.registered === true,
      }))
    : [];
  return {
    key: String(m.key ?? ''),
    map: String(m.map ?? '?'),
    startUtc: num(m.startUtc),
    endUtc: num(m.endUtc),
    durationSecs: num(m.durationSecs),
    players,
    bots: num(m.bots),
    replayKey: m.replayKey == null ? '' : String(m.replayKey),
    hasReplay: m.hasReplay === true,
    replaySize: num(m.replaySize),
  };
}
